import React from 'react';
import { MapPin, Building2 } from 'lucide-react';
import { SavedLocation } from '@hero/schema';
import { locationAutocompleteAPI } from '@hero/api';
import { MapSearchMode } from '../types';
import { LocationItem } from './LocationItem';

type SearchLocationData = locationAutocompleteAPI['data']['googlePlaces'][0];

export interface SearchResultsListProps {
  googlePlaces: SearchLocationData[];
  savedLocations: SavedLocation[];
  searchMode: MapSearchMode;
  onSelectGooglePlace: (place: SearchLocationData) => void;
  onSelectSavedLocation: (location: SavedLocation) => void;
  isRTL: boolean;
  t: (key: string, defaultValue?: string) => string;
}

export function SearchResultsList({
  googlePlaces,
  savedLocations,
  searchMode,
  onSelectGooglePlace,
  onSelectSavedLocation,
  isRTL,
  t,
}: SearchResultsListProps) {
  return (
    <div className="flex-1 overflow-y-auto min-h-0">
      {/* Saved Locations from Search */}
      {searchMode === 'google-and-saved-locations' && savedLocations.length > 0 && (
        <div className="p-2 border-b border-gray-200">
          <div className="flex items-center gap-2 px-2 py-1 mb-2">
            <Building2 className="h-4 w-4 text-blue-500" />
            <span className="text-xs font-semibold text-gray-500 uppercase">
              {t('location.savedLocations', 'Saved Locations')}
            </span>
          </div>
          {savedLocations.map(savedLocation => (
            <LocationItem
              key={String(savedLocation._id)}
              location={savedLocation}
              type="saved"
              onClick={() => onSelectSavedLocation(savedLocation)}
              isRTL={isRTL}
            />
          ))}
        </div>
      )}

      {/* Google Places */}
      {googlePlaces.length > 0 && (
        <div className="p-2 pb-4">
          <div className="flex items-center gap-2 px-2 py-1 mb-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase">
              {t('location.googlePlaces', 'Google Places')}
            </span>
          </div>
          {googlePlaces.map(place => (
            <LocationItem
              key={place.placeId}
              location={place}
              type="google"
              onClick={() => onSelectGooglePlace(place)}
              isRTL={isRTL}
            />
          ))}
        </div>
      )}

      {/* No Results */}
      {googlePlaces.length === 0 &&
        (searchMode === 'google-only' || savedLocations.length === 0) && (
          <div className={`px-4 py-2 text-sm text-gray-500 ${isRTL ? 'text-right' : ''}`}>
            {t('location.noLocationsFound', 'No locations found')}
          </div>
        )}
    </div>
  );
}
